function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useRef, useState } from 'react';
import { Fade, Label, Progress } from 'reactstrap';
import { v4 as uuidV4 } from 'uuid';
import PropTypes from 'prop-types';
import moment from 'moment';
import fileProcessor from '../../utils/fileProcessor';
import MultiPicker from '../common/controls/multiPicker';
import Upload from '../common/controls/upload';
import Loader from '../common/loader';
import { combineDocuments } from './documentSelectors';
import entityComposition from '../common/classes/entityComposition';
const DocumentsUploadPicker = ({
  document,
  documentActions,
  jobID: _jobID = '',
  label: _label = 'Library Documents',
  labelKey: _labelKey = 'displayName',
  multiple: _multiple = true,
  onChange,
  options,
  selectedValues: _selectedValues = [],
  userID,
  notificationActions,
  testUploadingDoc: _testUploadingDoc = false
}) => {
  const [uploadingDoc, setUploadingDoc] = useState(_testUploadingDoc);
  const {
    error,
    lastUploadedDocs,
    loading,
    pendingDocs,
    progress,
    failedToUpload
  } = document;
  const prevProgress = useRef(progress);
  const handleChange = React.useCallback((newValue, uploaded = false) => {
    let values = newValue;
    if (newValue && _multiple) {
      values = Array.isArray(newValue) ? values : [values];
    }

    // combine new values with old when this method is called with `uploaded` as true.
    values = uploaded && _multiple ? [..._selectedValues, ...values] : values;
    onChange(values, uploaded);
  }, [_multiple, onChange, _selectedValues]);
  React.useEffect(() => {
    // multiple pickers can coexist on a page, so need to track which instance is doing the upload
    if (!uploadingDoc) {
      return;
    }

    // watch for progress when uploading a document
    if (progress !== undefined // var defined
    && !loading // not loading
    && !error // no errors
    && progress === 95 // progress is at 95% (successfully got S3 downloadURL)
    ) {
      // libraryDocument was uploaded to S3. go ahead and save to server
      documentActions.saveAllSuccess(pendingDocs);
    } else if (progress // var defined
    && !loading // not loading
    && !error // no errors
    && progress === 100 // document upload is complete
    && prevProgress !== 100 // document upload was NOT complete, before this componentDidUpdate
    ) {
      // libraryDocument was successfully saved, add it has a selection
      notificationActions.upload({
        files: lastUploadedDocs,
        failedToUpload
      });
      const newSelectedDocs = lastUploadedDocs.map(doc => _objectSpread(_objectSpread({}, doc), {}, {
        libraryDocumentID: doc.id,
        name: doc.displayName
      }));
      handleChange(newSelectedDocs, true);

      // clear upload state
      if (uploadingDoc && !pendingDocs.length) {
        documentActions.uploadClear();
        setUploadingDoc(false);
      }
    }
  }, [documentActions, document, error, failedToUpload, handleChange, lastUploadedDocs, loading, notificationActions, pendingDocs, progress, uploadingDoc]);
  const handleUpload = async filesToUpload => {
    if (filesToUpload.length === 0) {
      return notificationActions.error({
        message: 'Must pass one ore more .PDF or .pdf files.',
        id: 'NO_FILES'
      });
    }
    const jobs = _jobID ? [{
      id: _jobID
    }] : [];
    const createdDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
    const validationFailureMessages = [];
    const filePromises = filesToUpload.map(async file => {
      try {
        const hashString = await fileProcessor.detectFileAndCreateMD5Hash(file);
        return {
          id: uuidV4().toUpperCase(),
          cloudDocumentID: uuidV4().toUpperCase(),
          remotePath: uuidV4().toUpperCase(),
          hashString
        };
      } catch (processingFailure) {
        if (processingFailure) {
          validationFailureMessages.push(processingFailure);
          throw processingFailure;
        }
        return {
          id: uuidV4().toUpperCase(),
          cloudDocumentID: uuidV4().toUpperCase(),
          remotePath: uuidV4().toUpperCase(),
          hashString: null
        };
      }
    });
    await setUploadingDoc(true);
    try {
      const ids = await Promise.all(filePromises);
      documentActions.create(ids, createdDate, filesToUpload, [], jobs, userID);
    } catch (_unused) {
      // we're building the array of ids and of validation failure messages
      // we don't want an error to throw us out of the upload process, we check for
      // validation failure messages below and change status and display user feedback as needed
    }
    if (validationFailureMessages.length) {
      notificationActions.error({
        autoClose: 10000,
        message: 'One or more files in the upload queue was encrypted, corrupted or not of type PDF. Please try again.',
        id: 'invalid_file_type'
      });
      await setUploadingDoc(false);
    }
    return undefined;
  };
  const preparedOptions = combineDocuments(options, _selectedValues);
  const progressComponent = progress > 0 ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Fade, {
    in: progress > -1 && progress < 100
  }, /*#__PURE__*/React.createElement(Progress, {
    active: "true",
    value: progress
  }))) : /*#__PURE__*/React.createElement(Progress, {
    value: 0
  });
  return /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '1em'
    }
  }, /*#__PURE__*/React.createElement(Upload, {
    onUpload: handleUpload,
    loading: false // (trs) intentionally false. we handle loading state below
    ,
    accept: {
      'application/pdf': ['.pdf']
    },
    errorText: "Only .PDF files are allowed",
    disabled: progress > 0 && progress < 100
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Label, null, _label), /*#__PURE__*/React.createElement(MultiPicker, {
    className: "t-doc-picker",
    entityActions: documentActions,
    entity: document,
    disabled: false,
    label: null,
    includeSelectionsForID: _jobID,
    labelKey: _labelKey,
    multi: _multiple,
    onChange: handleChange,
    options: preparedOptions,
    placeholder: "Select...",
    values: _selectedValues
  })), uploadingDoc ? /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: '100px'
    }
  }, /*#__PURE__*/React.createElement(Loader, null), progressComponent) : /*#__PURE__*/React.createElement("p", null, "Drag and drop one or more .PDF files here to upload new library documents.")));
};
DocumentsUploadPicker.propTypes = {
  document: PropTypes.shape({
    error: PropTypes.string,
    lastUploadedDocs: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool,
    pendingDocs: PropTypes.arrayOf(PropTypes.shape({})),
    failedToUpload: PropTypes.arrayOf(PropTypes.shape({})),
    progress: PropTypes.number
  }).isRequired,
  documentActions: PropTypes.shape({
    create: PropTypes.func,
    serverSaveAll: PropTypes.func,
    saveAllSuccess: PropTypes.func,
    serverUpload: PropTypes.func,
    uploadClear: PropTypes.func
  }).isRequired,
  // document redux actions
  jobID: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  multiple: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string
  })),
  userID: PropTypes.string.isRequired,
  notificationActions: PropTypes.shape({
    upload: PropTypes.func,
    error: PropTypes.func
  }).isRequired,
  testUploadingDoc: PropTypes.bool
};
export { DocumentsUploadPicker as TestDocumentsUploadPicker };
export default entityComposition(DocumentsUploadPicker, [], {}, ['notification']);